import { useEffect, useState, useRef } from "react";
import doubleThree from "../.././assets/images/thirty-three.webp"
import sectionTwoHero from "../.././assets/images/section-two-hero.png"
import Globe2 from "../.././assets/images/Globe2.png"
import FilePenLine from "../.././assets/images/FilePenLine.png"

// ---------------- COUNTER HOOK (scroll + slow) ----------------
const useScrollCounter = (target, duration = 5000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let animationFrameId;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          animateCount();
        } else {
          setCount(0);
          cancelAnimationFrame(animationFrameId);
        }
      },
      { threshold: 0.2 }
    );

    const animateCount = () => {
      let startTime = null;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / duration, 1);

        setCount(Math.floor(percentage * target));

        if (percentage < 1) {
          animationFrameId = requestAnimationFrame(step);
        } else {
          setCount(target);
        }
      };

      animationFrameId = requestAnimationFrame(step);
    };

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [target, duration]);

  return { count, ref };
};

const CircularProgress = ({ percentage }) => {
  const size = 130;
  const strokeWidth = 10;
  const radius = (size / 2) - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center w-[140px] h-[140px]">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#dd3333"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="bg-[#06213F] rounded-full flex items-center justify-center shadow-lg"
          style={{
            width: '45%',
            height: '45%'
          }}
        >
          <span className="text-primary-white text-xl font-bold">{percentage}%</span>
        </div>
      </div>
    </div>
  );
};

export default function TrustedVisaSection({ data = null }) {
  const expValue = data?.experience?.value ? parseInt(data.experience.value) : 34;
  const { count: count33, ref } = useScrollCounter(expValue, 2500);
  const thirtyThreeStyle = {
    backgroundImage: `url(${doubleThree})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  const statPercentage = data?.stat?.value ? parseInt(data.stat.value.replace('%', '')) : 100;

  return (
    <section ref={ref} className="bg-white md:py-4 pb-10  px-4 md:px-12 lg:px-8 font-manrope">

      <div className="max-w-[1200px] mx-auto w-full flex lg:flex-row md:flex-col flex-col gap-16 justify-between items-center">
        {/* Left Column */}
        <div className="lg:max-w-[48%]">
          <div className="flex items-center mb-1">
            <div className="text-[100px] md:text-[130px] font-extrabold" style={thirtyThreeStyle}>
              {count33}
            </div>
            <p className="ml-4 md:text-[22px] text-[16px] font-extrabold text-gray-700 leading-tight" dangerouslySetInnerHTML={{ __html: data?.experience?.label || "Years of Overseas<br />Recruitment & Support" }}>
            </p>
          </div>

          <div dangerouslySetInnerHTML={{ 
            __html: data?.title || `<h2 class="xl:text-[50px] md:text-4xl text-3xl font-extrabold text-[#06213F] leading-[1.3] md:leading-[1.1] tracking-tight">
            <span class="block mb-2">
              Trusted <span class="text-primary-red">Visa</span> &
            </span>
            <span class="text-primary-red">Emigration</span> solutions
          </h2>` 
          }}></div>

          <p className="mt-6 text-primary-gray font-medium leading-[28px] whitespace-pre-line">
            {data?.description || "There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of."}
          </p>

          <div className="grid sm:grid-cols-2 gap-8 mt-[28px]">
            {(data?.features && data.features.length > 0 ? data.features : [
              {
                title: "Content Needs to change",
                description: "Almost do am or limits hearts. Resolve parties but why she shewing.",
                icon_url: Globe2
              },
              {
                title: "Content Needs to change",
                description: "Regular do am or limits hearts. Resolve parties but why she shewing.",
                icon_url: FilePenLine
              }
            ]).map((feature, idx) => (
              <div key={idx} className="flex flex-col gap-2 items-start ">
                <div className="flex gap-4 items-center text-primary-red">
                  <img src={feature.icon_url || (idx === 0 ? Globe2 : FilePenLine)} alt={feature.title} className="w-[55px] h-[55px]" />
                  <h3 className="max-w-[60%] font-extrabold text-primary-navyblue text-[22px]">{feature.title}</h3>
                </div>
                <p className="font-medium text-[16px] text-primary-gray pt-3 whitespace-pre-line">{feature.description}</p>
              </div>
            ))}
          </div>

          {data?.cta?.link ? (
            <a href={data.cta.link}>
              <button className="mt-8 bg-primary-red hover:bg-red-700 text-primary-white font-extrabold py-4 px-6 rounded-none text-sm tracking-wider uppercase">
                • {data?.cta?.text || "DISCOVER MORE"} •
              </button>
            </a>
          ) : (
            <button className="mt-8 bg-primary-red hover:bg-red-700 text-primary-white font-extrabold py-4 px-6 rounded-none text-sm tracking-wider uppercase">
              • {data?.cta?.text || "DISCOVER MORE"} •
            </button>
          )}
        </div>

        {/* Right Column */}
        <div className="relative lg:w-[40%]">
          <img src={data?.image_url || sectionTwoHero} alt="Trusted Visa Section" className="w-full lg:h-auto md:h-[550px] object-cover" />
          <div className="absolute -bottom-6 md:-left-12 bg-white px-4 md:py-8 py-4 shadow-2xl md:w-52 text-center flex flex-col justify-center items-center">
            <h4 className="font-extrabold text-[21px] text-primary-navyblue mb-3">{data?.stat?.label || "Success Rate"}</h4>
            <div className="mx-auto">
              <CircularProgress percentage={statPercentage} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
